import { forwardRef, useId, type TextareaHTMLAttributes } from 'react'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, size = 'md', className = '', id, rows = 3, required, ...props }, ref) => {
    const autoId = useId()
    const textareaId = id ?? autoId
    const describedBy = error ? `${textareaId}-error` : hint ? `${textareaId}-hint` : undefined

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={textareaId} className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/65">
            {label}
            {required && (
              <>
                <span className="ml-0.5 text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only"> (required)</span>
              </>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={rows}
          aria-invalid={error ? 'true' : undefined}
          aria-required={required || undefined}
          aria-describedby={describedBy}
          className={`block w-full rounded-md border bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 dark:bg-white/5 dark:text-white ${sizeClasses[size]} ${
            error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-white/10'
          } ${className}`}
          {...props}
        />
        {error ? (
          <p id={`${textareaId}-error`} role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : (
          hint && (
            <p id={`${textareaId}-hint`} className="mt-1 text-sm text-gray-500 dark:text-white/45">
              {hint}
            </p>
          )
        )}
      </div>
    )
  },
)

Textarea.displayName = 'Textarea'
